import React, { useEffect, useState, useContext, useMemo } from 'react';
import {
  Alert, Button, Card, Col, Row, Segmented, Select, Spin, Statistic,
  Table, Tag, Tooltip, Typography, Empty, Modal,
} from 'antd';
import { InfoCircleOutlined, DownloadOutlined } from '@ant-design/icons';
import * as XLSX from 'xlsx';
import api from '../api';
import { UserContext } from '../App';

const { Title, Text, Paragraph } = Typography;


const money = v => v == null
  ? '—'
  : `${v < 0 ? '-' : ''}$${Math.abs(v).toLocaleString('en-US', { minimumFractionDigits: 0, maximumFractionDigits: 0 })}`;

const Money = ({ v, bold, signed }) => {
  if (v == null) return <Text type="secondary">—</Text>;
  const color = signed ? (v > 0 ? '#389e0d' : v < 0 ? '#cf1322' : undefined)
                       : (v < 0 ? '#cf1322' : undefined);
  return (
    <span style={{ color, fontWeight: bold ? 600 : undefined, fontVariantNumeric: 'tabular-nums' }}>
      {signed && v > 0 ? '+' : ''}{money(v)}
    </span>
  );
};

const hrs = v => v == null ? '—' : Number(v).toLocaleString(undefined,
  { minimumFractionDigits: 1, maximumFractionDigits: 1 });

export default function CostRevenuePage() {
  const currentUser = useContext(UserContext);
  // Executive financial access alone; role is not required for this read-only
  // view. The payroll upload page still requires admin, since it writes data.
  const allowed = (currentUser?.CanViewFinancials ?? 0) >= 3;

  const [groupBy,   setGroupBy]   = useState('client');
  const [status,    setStatus]    = useState([]);
  const [loading,   setLoading]   = useState(true);
  const [data,      setData]      = useState(null);
  const [team,      setTeam]      = useState([]);   // team names; empty = all
  const [checkDates, setCheckDates] = useState([]);
  const [fromCheck, setFromCheck] = useState(null);
  const [toCheck,   setToCheck]   = useState(null);

  // Load the check dates first and default to the most recent three, roughly
  // six weeks, rather than guessing at dates that may not exist.
  useEffect(() => {
    if (!allowed) { setLoading(false); return; }
    api.get('/api/payroll-analytics/periods')
      .then(r => {
        const list = r.data || [];
        setCheckDates(list);
        if (list.length) {
          setToCheck(list[0].CheckDate);
          setFromCheck(list[Math.min(2, list.length - 1)].CheckDate);
        } else {
          setLoading(false);
        }
      })
      .catch(() => { setCheckDates([]); setLoading(false); });
  }, [allowed]);

  useEffect(() => {
    if (!allowed || !fromCheck || !toCheck) return;
    setLoading(true);
    api.get('/api/payroll-analytics/cost-revenue',
            { params: { from_check: fromCheck, to_check: toCheck } })
      .then(r => setData(r.data))
      .catch(() => setData(null))
      .finally(() => setLoading(false));
  }, [allowed, fromCheck, toCheck]);

  const rows = useMemo(() => data?.rows ?? [], [data]);

  // Overhead is a back-office TEAM, not an individual's pay type. A salaried
  // lead on a client team is a cost of running that team, so they belong in its
  // direct cost rather than in the pot spread across every other team.
  const overhead = useMemo(() => rows.filter(r => !r.IsClientTeam), [rows]);

  const teamOptions = useMemo(() => (
    [...new Set(rows.map(r => r.TeamName).filter(Boolean))].sort()
      .map(t => ({ label: t, value: t }))
  ), [rows]);

  const statusOptions = useMemo(() => (
    [...new Set(rows.map(r => (r.PaycorStatus || '').trim()).filter(Boolean))]
      .sort((a, b) => a.localeCompare(b))
      .map(s => ({ label: s, value: s.toLowerCase() }))
  ), [rows]);

  const visible = useMemo(() => {
    let out = team.length ? rows.filter(r => team.includes(r.TeamName)) : rows;
    // Status filters the employee list only. The team and client views stay
    // whole, since a team's cost includes its leavers' final pay and hiding
    // them would understate it.
    if (status.length && groupBy === 'employee') {
      out = out.filter(r => status.includes((r.PaycorStatus || '').trim().toLowerCase()));
    }
    return out;
  }, [rows, team, status, groupBy]);

  const teamRows = useMemo(() => {
    const map = {};
    for (const r of visible) {
      const k = r.TeamName ?? '(No team)';
      if (!map[k]) {
        map[k] = {
          TeamName: k, Employees: 0, BillableHours: 0, EstRevenue: 0,
          Wages: 0, PTO: 0, CommissionBonus: 0, EmployerTaxes: 0,
          CarrierPremium: 0, BenefitsEmployeePaid: 0,
          NetBenefits: 0, TotalCost: 0, GrossMargin: 0,
          _anyRevenue: false, _anyBenefits: false,
        };
      }
      const t = map[k];
      t.Employees      += 1;
      t.BillableHours  += r.BillableHours || 0;
      t.Wages          += r.Wages || 0;
      t.PTO            += r.PTO || 0;
      t.CommissionBonus += r.CommissionBonus || 0;
      t.EmployerTaxes  += r.EmployerTaxes || 0;
      t.TotalCost      += r.TotalCost || 0;
      if (r.EstRevenue  != null) { t.EstRevenue  += r.EstRevenue;  t._anyRevenue  = true; }
      t.BenefitsEmployeePaid += r.BenefitsEmployeePaid || 0;
      // Carrier premium is null when no invoice covers the person, which is
      // not the same as a zero premium, so it only accumulates when known.
      if (r.CarrierPremium != null) { t.CarrierPremium += r.CarrierPremium; }
      if (r.NetBenefits != null) { t.NetBenefits += r.NetBenefits; t._anyBenefits = true; }
      if (r.GrossMargin != null) { t.GrossMargin += r.GrossMargin; }
    }
    return Object.values(map).map(t => ({
      ...t,
      EstRevenue:  t._anyRevenue  ? Math.round(t.EstRevenue)  : null,
      CarrierPremium:       t._anyBenefits ? Math.round(t.CarrierPremium) : null,
      BenefitsEmployeePaid: Math.round(t.BenefitsEmployeePaid),
      NetBenefits: t._anyBenefits ? Math.round(t.NetBenefits) : null,
      GrossMargin: t._anyRevenue  ? Math.round(t.GrossMargin) : null,
    })).sort((a, b) => a.TeamName.localeCompare(b.TeamName));
  }, [visible]);

  // DirectCost is the cost of people on client teams only. It is tracked
  // alongside TotalCost rather than replacing it because the two answer
  // different questions, and the tables below still show both.
  const totals = useMemo(() => visible.reduce((a, r) => ({
    BillableHours: a.BillableHours + (r.BillableHours || 0),
    EstRevenue:    a.EstRevenue    + (r.EstRevenue    || 0),
    TotalCost:     a.TotalCost     + (r.TotalCost     || 0),
    DirectCost:    a.DirectCost    + (r.IsClientTeam ? (r.TotalCost || 0) : 0),
    GrossMargin:   a.GrossMargin   + (r.GrossMargin   || 0),
  }), { BillableHours: 0, EstRevenue: 0, TotalCost: 0, DirectCost: 0, GrossMargin: 0 }), [visible]);

  const rateMissingRows     = useMemo(() => visible.filter(r => r.RateMissing), [visible]);
  const benefitsMissingRows = useMemo(() => visible.filter(r => r.BenefitsMissing), [visible]);
  const rateMissing     = rateMissingRows.length;
  const benefitsMissing = benefitsMissingRows.length;

  // Each coverage count opens the list behind it. A number alone says how much
  // is wrong without saying what to act on, and the answer was otherwise a
  // hand-written query.
  const [whoModal, setWhoModal] = useState(null);   // { title, note, rows }


  // Revenue less direct cost, before any back-office cost is absorbed. Derived
  // from the two figures shown on the cards beside it so the four scorecards
  // reconcile on their face -- a margin that does not equal the revenue and
  // cost printed next to it reads as an error even when it is not.
  const directMargin = totals.EstRevenue - totals.DirectCost;
  const marginPct = totals.EstRevenue > 0
    ? (directMargin / totals.EstRevenue * 100) : null;

  const moneyCol = (title, key, opts = {}) => ({
    title, dataIndex: key, key, width: opts.width ?? 115, align: 'right',
    sorter: (a, b) => (a[key] ?? 0) - (b[key] ?? 0),
    render: v => <Money v={v} bold={opts.bold} signed={opts.signed} />,
  });

  const sharedCols = [
    { title: 'Billable Hrs', dataIndex: 'BillableHours', key: 'BillableHours',
      width: 110, align: 'right',
      sorter: (a, b) => (a.BillableHours ?? 0) - (b.BillableHours ?? 0),
      render: v => hrs(v) },
    moneyCol('Est Revenue', 'EstRevenue'),
    moneyCol('Wages', 'Wages'),
    moneyCol('PTO', 'PTO', { width: 100 }),
    moneyCol('Comm / Bonus', 'CommissionBonus'),
    moneyCol('Employer Taxes', 'EmployerTaxes', { width: 125 }),
    // Split so the arithmetic is auditable: Benefit Amt is the carrier
    // premium prorated into the window, Benefit Deduction is the 6040
    // employee-paid offset from the GL, and Total Cost uses the difference.
    moneyCol('Benefit Amt', 'CarrierPremium', { width: 120 }),
    moneyCol('Benefit Deduction', 'BenefitsEmployeePaid', { width: 140 }),
    moneyCol('Net Benefits', 'NetBenefits', { width: 120 }),
    moneyCol('Total Cost', 'TotalCost', { bold: true, width: 120 }),
    {
      title: (
        <Tooltip title="Revenue less this row's own cost.">
          Margin (direct) <InfoCircleOutlined style={{ fontSize: 11, color: '#888' }} />
        </Tooltip>
      ),
      dataIndex: 'GrossMargin', key: 'GrossMargin',
      width: 135, align: 'right',
      sorter: (a, b) => (a.GrossMargin ?? 0) - (b.GrossMargin ?? 0),
      // Rows with no billable rate have their revenue counted as zero, so the
      // margin is a worst case rather than a fact. Marked so it is not read as
      // a real loss.
      render: (v, r) => r.RateMissing
        ? (
          <Tooltip title="No billable rate for this job and team, so revenue counts as zero. This margin is a worst case, not a measured figure.">
            <span style={{ borderBottom: '1px dashed #d46b08', cursor: 'help' }}>
              <Money v={v} bold signed />
            </span>
          </Tooltip>
        )
        : <Money v={v} bold signed />,
    },
    {
      title: 'Margin % (direct)', key: 'MarginPct', width: 130, align: 'right',
      sorter: (a, b) => {
        const pa = a.EstRevenue > 0 ? a.GrossMargin / a.EstRevenue : -Infinity;
        const pb = b.EstRevenue > 0 ? b.GrossMargin / b.EstRevenue : -Infinity;
        return pa - pb;
      },
      render: (_, r) => {
        if (!r.EstRevenue || r.GrossMargin == null) return <Text type="secondary">—</Text>;
        const p = r.GrossMargin / r.EstRevenue * 100;
        return <strong style={{ color: p >= 0 ? '#389e0d' : '#cf1322' }}>{p.toFixed(1)}%</strong>;
      },
    },
  ];

  const alloc       = data?.allocation;
  const clientTeams = alloc?.client_teams ?? [];

  const clientColumns = [
    { title: 'Client Team', dataIndex: 'TeamName', key: 'TeamName', width: 170, fixed: 'left',
      sorter: (a, b) => a.TeamName.localeCompare(b.TeamName),
      render: v => <Text strong>{v}</Text> },
    { title: 'Heads', dataIndex: 'Headcount', key: 'Headcount', width: 80, align: 'right' },
    {
      title: (
        <Tooltip title="Scheduled hours divided by 40. Where an assignment has no scheduled hours, Paycor's full-time flag is used instead: 1.0 full time, 0.5 part time. A dotted underline marks a team whose FTE includes such estimates.">
          FTE <InfoCircleOutlined style={{ fontSize: 11, color: '#888' }} />
        </Tooltip>
      ),
      dataIndex: 'FTE', key: 'FTE', width: 80, align: 'right',
      sorter: (a, b) => a.FTE - b.FTE,
      render: (v, r) => {
        const est = r.FTEEstimated || 0;
        const txt = Number(v).toFixed(1);
        if (!est) return txt;
        return (
          <Tooltip title={`${est} of ${r.Headcount} have no scheduled hours set, so their FTE is estimated from Paycor's full-time flag.`}>
            <span style={{ borderBottom: '1px dotted #bbb', cursor: 'help' }}>{txt}</span>
          </Tooltip>
        );
      },
    },
    {
      title: (
        <Tooltip title="Share of total client FTE, and the proportion of back-office cost this team absorbs">
          Share <InfoCircleOutlined style={{ fontSize: 11, color: '#888' }} />
        </Tooltip>
      ),
      dataIndex: 'SharePct', key: 'SharePct', width: 90, align: 'right',
      sorter: (a, b) => a.SharePct - b.SharePct,
      render: v => `${Number(v).toFixed(1)}%`,
    },
    { title: 'Billable Hrs', dataIndex: 'BillableHours', key: 'BillableHours',
      width: 110, align: 'right',
      sorter: (a, b) => a.BillableHours - b.BillableHours, render: v => hrs(v) },
    moneyCol('Est Revenue', 'EstRevenue'),
    moneyCol('Direct Cost', 'DirectCost'),
    {
      title: (
        <Tooltip title="Revenue less the team's own cost.">
          Margin (direct) <InfoCircleOutlined style={{ fontSize: 11, color: '#888' }} />
        </Tooltip>
      ),
      dataIndex: 'MarginBefore', key: 'MarginBefore', width: 135, align: 'right',
      sorter: (a, b) => (a.MarginBefore ?? 0) - (b.MarginBefore ?? 0),
      render: v => <Money v={v} signed />,
    },
    {
      title: 'Margin % (direct)', key: 'MarginPctDirect', width: 130, align: 'right',
      sorter: (a, b) => {
        const pa = a.EstRevenue > 0 ? a.MarginBefore / a.EstRevenue : -Infinity;
        const pb = b.EstRevenue > 0 ? b.MarginBefore / b.EstRevenue : -Infinity;
        return pa - pb;
      },
      render: (_, r) => {
        if (!r.EstRevenue || r.MarginBefore == null) return <Text type="secondary">—</Text>;
        const p = r.MarginBefore / r.EstRevenue * 100;
        return <strong style={{ color: p >= 0 ? '#389e0d' : '#cf1322' }}>{p.toFixed(1)}%</strong>;
      },
    },
  ];

  const teamColumns = [
    { title: 'Team', dataIndex: 'TeamName', key: 'TeamName', width: 170, fixed: 'left',
      sorter: (a, b) => a.TeamName.localeCompare(b.TeamName),
      render: v => <Text strong>{v}</Text> },
    { title: 'Employees', dataIndex: 'Employees', key: 'Employees', width: 100, align: 'right' },
    ...sharedCols,
  ];

  const employeeColumns = [
    {
      title: 'Employee', key: 'Name', width: 230, fixed: 'left',
      sorter: (a, b) => (a.Name ?? '').localeCompare(b.Name ?? ''),
      render: (_, r) => {
        // Paycor status explains the rows with no hours and a token cost:
        // a final payout arriving after someone has left.
        const s    = (r.PaycorStatus || '').trim();
        const gone = ['resigned', 'terminated', 'retired', 'laidoff']
          .includes(s.toLowerCase());
        return (
          <div>
            <div style={{ fontWeight: 600, fontSize: 13 }}>{r.Name || '(no name)'}</div>
            <div style={{ display: 'flex', alignItems: 'center', gap: 6 }}>
              <Text type="secondary" style={{ fontSize: 11, fontFamily: 'monospace' }}>
                {r.EmployeeNumber}
              </Text>
              {s && (
                <Tooltip title={r.TermDate ? `Termed ${r.TermDate}` : 'Paycor status'}>
                  <Tag
                    color={gone ? 'red' : 'green'}
                    style={{ fontSize: 10, lineHeight: '16px', marginInlineEnd: 0, textTransform: 'capitalize' }}
                  >
                    {s}
                  </Tag>
                </Tooltip>
              )}
            </div>
          </div>
        );
      },
    },
    { title: 'Team', dataIndex: 'TeamName', key: 'TeamName', width: 150,
      sorter: (a, b) => (a.TeamName ?? '').localeCompare(b.TeamName ?? ''),
      render: v => v || <Tag color="orange">No team</Tag> },
    ...sharedCols,
  ];

  // Export the view on screen with its filters, as plain numbers (money
  // rounded to cents) so the sheet can be summed.
  const exportExcel = () => {
    const num = (v) => (v == null || v === '' ? '' : Math.round(Number(v) * 100) / 100);
    const pct = (m, rev) => (rev > 0 && m != null ? Math.round(m / rev * 1000) / 10 : '');
    const shared = (r) => ({
      'Billable Hrs':      num(r.BillableHours),
      'Est Revenue':       num(r.EstRevenue),
      'Wages':             num(r.Wages),
      'PTO':               num(r.PTO),
      'Comm / Bonus':      num(r.CommissionBonus),
      'Employer Taxes':    num(r.EmployerTaxes),
      'Benefit Amt':       num(r.CarrierPremium),
      'Benefit Deduction': num(r.BenefitsEmployeePaid),
      'Net Benefits':      num(r.NetBenefits),
      'Total Cost':        num(r.TotalCost),
      'Margin (direct)':   num(r.GrossMargin),
      'Margin % (direct)': pct(r.GrossMargin, r.EstRevenue),
    });
    let out; let sheet;
    if (groupBy === 'client') {
      sheet = 'Client Teams';
      out = clientTeams.map((r) => ({
        'Client Team':       r.TeamName,
        'Heads':             r.Headcount,
        'FTE':               num(r.FTE),
        'Share %':           num(r.SharePct),
        'Billable Hrs':      num(r.BillableHours),
        'Est Revenue':       num(r.EstRevenue),
        'Direct Cost':       num(r.DirectCost),
        'Margin (direct)':   num(r.MarginBefore),
        'Margin % (direct)': pct(r.MarginBefore, r.EstRevenue),
      }));
    } else if (groupBy === 'team') {
      sheet = 'By Team';
      out = teamRows.map((r) => ({ 'Team': r.TeamName, 'Employees': r.Employees, ...shared(r) }));
    } else {
      sheet = 'By Employee';
      out = visible.map((r) => ({
        'Employee':      r.Name || '',
        'Employee #':    r.EmployeeNumber || '',
        'Paycor Status': (r.PaycorStatus || '').trim(),
        'Term Date':     r.TermDate || '',
        'Team':          r.TeamName || '',
        ...shared(r),
      }));
    }
    const ws = XLSX.utils.json_to_sheet(out);
    const wb = XLSX.utils.book_new();
    XLSX.utils.book_append_sheet(wb, ws, sheet);
    XLSX.writeFile(wb, `cost-vs-revenue-${sheet.toLowerCase().replace(/ /g, '-')}-${fromCheck}-to-${toCheck}.xlsx`);
  };

  if (!allowed) {
    return (
      <div style={{ padding: '16px 20px' }}>
        <Alert type="error" showIcon
          message="Executive financial permission is required to view this page." />
      </div>
    );
  }

  const w = data?.window;

  return (
    <div style={{ padding: '16px 20px' }}>
      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center',
                    marginBottom: 16, gap: 16, flexWrap: 'wrap' }}>
        <div>
          <Title level={4} style={{ margin: 0 }}>Cost vs Revenue</Title>
          {w && (
            <Text type="secondary" style={{ fontSize: 12 }}>
              Checks {w.from_check} to {w.to_check} · covering work {w.start} to {w.end}
              {' '}· {w.periods} pay period{w.periods === 1 ? '' : 's'}
            </Text>
          )}
        </div>
        <div style={{ display: 'flex', gap: 12, alignItems: 'center', flexWrap: 'wrap' }}>
          {/* Check dates, not arbitrary dates: only these exist in payroll,
              and each option shows the period it pays for since the two differ */}
          <Select
            value={fromCheck}
            onChange={setFromCheck}
            style={{ minWidth: 150 }}
            placeholder="From check"
            options={checkDates.map(c => ({
              value: c.CheckDate,
              label: c.CheckDate,
              title: c.PeriodStart ? `Pays ${c.PeriodStart} to ${c.PeriodEnd}` : undefined,
            }))}
          />
          <Text type="secondary" style={{ fontSize: 12 }}>to</Text>
          <Select
            value={toCheck}
            onChange={setToCheck}
            style={{ minWidth: 150 }}
            placeholder="To check"
            options={checkDates.map(c => ({
              value: c.CheckDate,
              label: c.CheckDate,
              title: c.PeriodStart ? `Pays ${c.PeriodStart} to ${c.PeriodEnd}` : undefined,
            }))}
          />
          <Select mode="multiple" maxTagCount="responsive" value={team} onChange={setTeam} options={teamOptions} allowClear
                  placeholder="All teams" style={{ minWidth: 220 }} />
          {groupBy === 'employee' && (
            <Select
              mode="multiple"
              value={status}
              onChange={setStatus}
              options={statusOptions}
              allowClear
              maxTagCount="responsive"
              placeholder="All statuses"
              style={{ minWidth: 190 }}
            />
          )}
          <Segmented value={groupBy} onChange={setGroupBy}
            options={[{ label: 'Client Teams', value: 'client' },
                      { label: 'By Team', value: 'team' },
                      { label: 'By Employee', value: 'employee' }]} />
          <Button icon={<DownloadOutlined />} onClick={exportExcel}
                  disabled={loading || !(groupBy === 'client' ? clientTeams.length
                    : (groupBy === 'team' ? teamRows.length : visible.length))}>
            Export
          </Button>
        </div>
      </div>

      <Spin spinning={loading} tip="Loading cost and revenue…" size="large">
        {data?.warning && (
          <Alert type="warning" showIcon style={{ marginBottom: 16 }} message={data.warning} />
        )}

        {!loading && rows.length > 0 && (
          <>
            <Row gutter={[12, 12]} style={{ marginBottom: 16 }}>
              <Col xs={24} sm={12} lg={6}>
                <Card size="small" styles={{ body: { padding: '8px 12px' } }}>
                  <Statistic title="Billable Hours" value={hrs(totals.BillableHours)} />
                </Card>
              </Col>
              <Col xs={24} sm={12} lg={6}>
                <Card size="small" styles={{ body: { padding: '8px 12px' } }}>
                  <Statistic title="Est Revenue" value={money(totals.EstRevenue)} />
                </Card>
              </Col>
              <Col xs={24} sm={12} lg={6}>
                <Card size="small" styles={{ body: { padding: '8px 12px' } }}>
                  <Statistic
                    title={(
                      <Tooltip title={`Cost of everyone on a client team, salaried staff included. It leaves out the back-office teams, whose cost is absorbed into the client teams below. Everyone together costs ${money(totals.TotalCost)}.`}>
                        <span style={{ borderBottom: '1px dotted #bfbfbf', cursor: 'help' }}>
                          Direct Cost <InfoCircleOutlined style={{ fontSize: 11, color: '#888' }} />
                        </span>
                      </Tooltip>
                    )}
                    value={money(totals.DirectCost)}
                  />
                </Card>
              </Col>
              <Col xs={24} sm={12} lg={6}>
                <Card size="small" styles={{ body: { padding: '8px 12px' } }}>
                  <Statistic
                    title={(
                      <Tooltip title="Estimated revenue less direct cost, before back-office cost is absorbed. This is what the client teams earn on their own work; the absorbed margin in the table below is what is left once overhead is carried.">
                        <span style={{ borderBottom: '1px dotted #bfbfbf', cursor: 'help' }}>
                          {marginPct == null ? 'Direct Gross Margin' : `Direct Gross Margin (${marginPct.toFixed(1)}%)`}
                          <InfoCircleOutlined style={{ fontSize: 11, color: '#888', marginLeft: 4 }} />
                        </span>
                      </Tooltip>
                    )}
                    value={money(directMargin)}
                    valueStyle={{ color: directMargin >= 0 ? '#389e0d' : '#cf1322' }}
                  />
                </Card>
              </Col>
            </Row>

            {(rateMissing > 0 || benefitsMissing > 0 || overhead.length > 0) && (
              <Alert
                type="info"
                showIcon
                style={{ marginBottom: 16 }}
                message="Coverage"
                description={
                  <div style={{ fontSize: 12 }}>
                    {rateMissing > 0 && (
                      <div>
                        <Tag color="orange" style={{ cursor: 'pointer' }}
                          onClick={() => setWhoModal({
                            title: 'No billable rate',
                            note: 'No rate is configured for this job and team, so revenue shows as a dash rather than zero.',
                            rows: rateMissingRows,
                          })}>{rateMissing}</Tag>
                        employee{rateMissing === 1 ? '' : 's'} have no billable rate for their
                        job and team, so their revenue shows as a dash rather than zero -- a
                        missing rate is not the same as billing nothing.
                      </div>
                    )}
                    {benefitsMissing > 0 && (
                      <div style={{ marginTop: 4 }}>
                        <Tag color="orange" style={{ cursor: 'pointer' }}
                          onClick={() => setWhoModal({
                            title: 'No carrier premium linked',
                            note: 'Full-time staff with no premium on the Principal or BCBS pages. Their cost excludes benefits and is understated.',
                            rows: benefitsMissingRows,
                          })}>{benefitsMissing}</Tag>
                        full-time employee{benefitsMissing === 1 ? '' : 's'} have no carrier
                        premium linked, so their cost excludes benefits and is understated.
                        Link them on the Principal and BCBS pages. Not counted here:
                        part-time staff, who are not offered benefits; recent starters,
                        who are covered from the 1st of the month after their first full
                        month; and anyone who left before that date. For all three, having
                        no premium is correct and their cost is complete.
                      </div>
                    )}
                    {/* The count, not the cost. These people are absent from
                        every client team's figures, which is worth knowing;
                        what their overhead totals is not shown on this page.
                        The API still returns it, so the absorption view can
                        come back without rebuilding anything. */}
                    {overhead.length > 0 && (
                      <div style={{ marginTop: 4 }}>
                        <Tag style={{ cursor: 'pointer' }}
                          onClick={() => setWhoModal({
                            title: 'Back-office staff',
                            note: 'Not on a client team, so they are not counted in any client team below.',
                            rows: overhead,
                          })}>{overhead.length}</Tag>
                        {overhead.length === 1 ? 'person sits' : 'people sit'} on back-office
                        teams, so they appear in no client team below. Salaried staff on a client
                        team are that team's direct cost.
                      </div>
                    )}

                  </div>
                }
              />
            )}

            {groupBy === 'client' ? (
              <Table
                rowKey="TeamName"
                dataSource={clientTeams}
                columns={clientColumns}
                size="small"
                pagination={false}
                bordered
                sticky
                scroll={{ x: 'max-content' }}
                locale={{ emptyText: 'No teams are flagged as client teams. Set IsClientTeam on the Teams admin page.' }}
                summary={() => {
                  if (!clientTeams.length) return null;
                  const sum = (k) => clientTeams.reduce((s, r) => s + (r[k] || 0), 0);
                  const rev = sum('EstRevenue');
                  let i = 1;
                  const cells = [
                    <Table.Summary.Cell key="lead" index={0}><strong>Total</strong></Table.Summary.Cell>,
                  ];
                  const push = (node) => {
                    cells.push(
                      <Table.Summary.Cell key={i} index={i} align="right">
                        <strong>{node}</strong>
                      </Table.Summary.Cell>);
                    i += 1;
                  };
                  push(sum('Headcount'));
                  push(sum('FTE').toFixed(1));
                  push('100.0%');
                  push(hrs(sum('BillableHours')));
                  push(<Money v={Math.round(rev)} />);
                  push(<Money v={Math.round(sum('DirectCost'))} />);
                  // Positional cells: one per column, in order.
                  const marBefore = sum('MarginBefore');
                  push(<Money v={Math.round(marBefore)} signed />);
                  push(rev > 0
                    ? <span style={{ color: marBefore >= 0 ? '#389e0d' : '#cf1322' }}>
                        {(marBefore / rev * 100).toFixed(1)}%
                      </span>
                    : '—');
                  return (
                    <Table.Summary fixed="top">
                      <Table.Summary.Row>{cells}</Table.Summary.Row>
                    </Table.Summary>
                  );
                }}
              />
            ) : (
            <Table
              rowKey={groupBy === 'team' ? 'TeamName' : 'EmployeeNumber'}
              dataSource={groupBy === 'team' ? teamRows : visible}
              columns={groupBy === 'team' ? teamColumns : employeeColumns}
              size="small"
              pagination={false}
              bordered
              sticky
              scroll={{ x: 'max-content' }}
              summary={() => {
                const src = groupBy === 'team' ? teamRows : visible;
                const sum = (k) => src.reduce((s, r) => s + (r[k] || 0), 0);
                const rev = sum('EstRevenue');
                const mar = sum('GrossMargin');
                let i = groupBy === 'team' ? 2 : 2;
                const cells = [
                  <Table.Summary.Cell key="lead" index={0} colSpan={2}>
                    <strong>Total</strong>
                  </Table.Summary.Cell>,
                ];
                const push = (node) => {
                  cells.push(
                    <Table.Summary.Cell key={i} index={i} align="right">
                      <strong>{node}</strong>
                    </Table.Summary.Cell>);
                  i += 1;
                };
                push(hrs(sum('BillableHours')));
                push(<Money v={Math.round(rev)} />);
                push(<Money v={Math.round(sum('Wages'))} />);
                push(<Money v={Math.round(sum('PTO'))} />);
                push(<Money v={Math.round(sum('CommissionBonus'))} />);
                push(<Money v={Math.round(sum('EmployerTaxes'))} />);
                push(<Money v={Math.round(sum('CarrierPremium'))} />);
                push(<Money v={Math.round(sum('BenefitsEmployeePaid'))} />);
                push(<Money v={Math.round(sum('NetBenefits'))} />);
                push(<Money v={Math.round(sum('TotalCost'))} bold />);
                push(<Money v={Math.round(mar)} bold signed />);
                push(rev > 0
                  ? <span style={{ color: mar >= 0 ? '#389e0d' : '#cf1322' }}>
                      {(mar / rev * 100).toFixed(1)}%
                    </span>
                  : '—');
                return (
                  <Table.Summary fixed="top">
                    <Table.Summary.Row>{cells}</Table.Summary.Row>
                  </Table.Summary>
                );
              }}
            />
            )}

            {groupBy === 'client' && alloc && (
              <Alert
                type="info"
                showIcon
                style={{ marginTop: 12 }}
                // The pool is two parts: the payroll of people off client
                // teams, and monthly amounts entered on their own
                // (OverheadCosts). Shown apart so the total explains itself --
                // "from 13 people" alone read as if they cost all of it.
                message={
                  <span>
                    <strong>{money(alloc.overhead_cost)}</strong> of back-office cost allocated across{' '}
                    <strong>{alloc.client_fte}</strong> client FTE
                  </span>
                }
                description={
                  <span style={{ fontSize: 12 }}>
                    <div style={{ marginBottom: 6 }}>
                      <strong>{money(alloc.payroll_overhead)}</strong> payroll of{' '}
                      <strong>{alloc.overhead_heads}</strong> {alloc.overhead_heads === 1 ? 'person' : 'people'} on back-office teams
                      {alloc.entered_overhead > 0 && (
                        <>
                          {' '}+ <strong>{money(alloc.entered_overhead)}</strong> entered overhead
                          {(alloc.entered_by_category || []).length > 0 && (
                            <> ({alloc.entered_by_category.map((c) => `${c.Category} ${money(c.Amount)}`).join(', ')})</>
                          )}
                        </>
                      )}
                    </div>
                    Every team not flagged as a client team is treated as overhead, including
                    anyone with no team, so all of it is absorbed and the team margins sum back
                    to company margin. Shares are FTE-weighted rather than headcount, so a
                    part-timer does not absorb the same as someone full time. A salaried lead on
                    a client team counts as that team's direct cost, not overhead.
                  </span>
                }
              />
            )}

            <Paragraph type="secondary" style={{ fontSize: 12, marginTop: 12, maxWidth: 900 }}>
              <InfoCircleOutlined /> Wages, taxes and billable hours are exact for the window --
              a paycheck covers precisely its pay period and timecards sum over the same days.
              Benefits are the one estimate: monthly carrier premiums are prorated by day, net of
              the employee contribution. Prorating daily rather than splitting a premium across
              each check keeps the annual total exact, since biweekly pay gives 26 checks across
              12 months.
            </Paragraph>
          </>
        )}

        {!loading && rows.length === 0 && !data?.warning && (
          <Empty description="No payroll data in the selected window." style={{ padding: 48 }} />
        )}
      </Spin>

      {/* Who is behind a coverage count. Read-only, and the same columns for
          all three lists, since the question is always the same: which people,
          on which team, and how much is at stake. */}
      <Modal maskClosable={false}
        open={!!whoModal}
        title={whoModal?.title}
        onCancel={() => setWhoModal(null)}
        footer={null}
        width={760}
      >
        {whoModal?.note && (
          <Typography.Paragraph type="secondary" style={{ fontSize: 13 }}>
            {whoModal.note}
          </Typography.Paragraph>
        )}
        <Table
          rowKey="EmployeeNumber"
          size="small"
          dataSource={whoModal?.rows || []}
          pagination={{ defaultPageSize: 15, hideOnSinglePage: true, showTotal: (t) => `${t} people` }}
          columns={[
            { title: 'Name', dataIndex: 'Name', key: 'Name', ellipsis: true },
            { title: 'Team', dataIndex: 'TeamName', key: 'TeamName', ellipsis: true,
              render: (v) => v || <Typography.Text type="secondary">(none)</Typography.Text> },
            { title: 'Job', dataIndex: 'JobTitle', key: 'JobTitle', ellipsis: true },
            { title: 'FTE', dataIndex: 'FTE', key: 'FTE', width: 70, align: 'right',
              render: (v) => v == null ? '—' : Number(v).toFixed(2) },
            { title: 'Benefits from', dataIndex: 'BenefitsEligibleFrom', key: 'BenefitsEligibleFrom',
              width: 120,
              render: (v) => v || <Typography.Text type="secondary">—</Typography.Text> },
            { title: 'Total Cost', dataIndex: 'TotalCost', key: 'TotalCost', width: 110, align: 'right',
              render: (v) => v == null ? '—' : money(Math.round(v)) },
          ]}
        />
      </Modal>
    </div>
  );
}
